/**
 * AttentionPage (v1: ReadinessPage)
 * Full-page attention surface: every item grouped by its server-assigned band (BR-13),
 * with Resolve / Dismiss / Snooze actions. Dismiss is hidden — disabled with an explanatory
 * tooltip — for items carrying a `statutoryDeadline` (BR-17); snooze stays available.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { RotateCcw, ClipboardList } from 'lucide-react';
import { formatTimestamp, asIsoDateTimeString } from '@resida/shared';
import { daysOverdue } from '../utils/date';
import {
  getAttentionItems,
  dismissAttentionItem,
  snoozeAttentionItem,
  removeAttentionItemPreference,
  type AttentionItem,
  type AttentionItemsResponse,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/layout/EmptyState';
import { ATTENTION_BAND_ORDER } from '@resida/api-types/attention';

// Shared react-query key with AppShell's `attentionCount` nav-badge query (BR-23) — kept as a
// literal (not a shared import) to avoid a new cross-cutting module for one string, but the two
// call sites must stay in sync: apps/landlord/src/components/navigation/AppShell.tsx.
const ATTENTION_ITEMS_QUERY_KEY = ['attention-items-summary'];

/* ── Constants ── */

type AttentionBand = AttentionItem['band'];

const BAND_LABELS: Record<AttentionBand, string> = {
  act_now: 'Needs attention now',
  waiting_on_other: 'Waiting on others',
  upcoming: 'Coming up',
  backlog: 'When you get to it',
};

const SEVERITY_COLORS = {
  required: 'text-destructive',
  recommended: 'text-warning',
  optional: 'text-muted-foreground',
} as const;

const SEVERITY_LABELS = {
  required: 'Required',
  recommended: 'Recommended',
  optional: 'Optional',
} as const;

const SNOOZE_OPTIONS: { days: 1 | 3 | 7 | 30; label: string }[] = [
  { days: 1, label: '1 day' },
  { days: 3, label: '3 days' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
];

const DEADLINE_DISMISS_TOOLTIP = 'This item has a legal deadline and cannot be dismissed. You can still snooze it.';

// BR-31 — a non-suppressible item (`suppressible === false`) must never offer dismiss OR snooze;
// the reason is always visible text (not only a `title`), same rationale as the deadline case.
const NON_SUPPRESSIBLE_REASON = "This item stays until it's resolved, so it can't be dismissed or snoozed.";

/* ── Trailing context (due/overdue/amount/count) — ported from DashboardPage.tsx's
   AttentionItemTrailing so the full-list page satisfies US-7/US-8/US-10 the same way the
   dashboard preview already does. ── */

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

// No per-item property timezone is available on an AttentionItem — the browser's own zone is
// the least-wrong default for a relative "due" label, routed through `formatTimestamp` (never a
// raw `.toLocaleString()`) so the real IANA zone is explicit at the call site.
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Trailing context for a row — amount, then overdue/due date, then a bare count (BR-12: never
 *  render a bare count when a richer field is present). Never invents zero for an absent field. */
function AttentionItemTrailing({ item }: { item: AttentionItem }) {
  const dateIso = item.statutoryDeadline ?? item.dueAt;
  if (item.amountCents == null && item.overdueSince == null && dateIso == null && item.count == null) {
    return null;
  }
  return (
    <div className="text-right shrink-0" data-testid={`attention-item-trailing-${item.taskHash}`}>
      {item.amountCents != null && (
        <span className="block text-sm font-medium text-foreground tabular-nums">
          {formatCurrency(item.amountCents)}
        </span>
      )}
      {item.overdueSince != null ? (
        <p className="text-xs text-muted-foreground">{daysOverdue(item.overdueSince)}d overdue</p>
      ) : dateIso != null ? (
        <p className="text-xs text-muted-foreground">
          Due {formatTimestamp(asIsoDateTimeString(dateIso), LOCAL_TZ, { month: 'short', day: 'numeric' })}
        </p>
      ) : item.amountCents == null && item.count != null ? (
        <p className="text-xs text-muted-foreground">{item.count}</p>
      ) : null}
    </div>
  );
}

/* ── Item Row ── */

interface ItemRowProps {
  item: AttentionItem;
  isPending: boolean;
  onDismiss: (taskHash: string) => void;
  onSnooze: (taskHash: string, days: 1 | 3 | 7 | 30) => void;
  onUndismiss: (taskHash: string) => void;
  showActions: boolean;
}

function ItemRow({ item, isPending, onDismiss, onSnooze, onUndismiss, showActions }: ItemRowProps) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [activeSnoozeIndex, setActiveSnoozeIndex] = useState(0);
  const snoozeTriggerRef = useRef<HTMLButtonElement>(null);
  const snoozeListboxRef = useRef<HTMLUListElement>(null);
  const isHidden = item.preference !== null;
  const severityColor = SEVERITY_COLORS[item.severity];
  // BR-31 — a non-suppressible item (BR-37's engine-level flag) can be neither dismissed nor
  // snoozed at all; BR-17's deadline-bearing item can still be snoozed, only dismiss is disabled.
  const nonSuppressible = item.suppressible === false;
  // BR-17 — a deadline-bearing item must never look dismissible and then fail; the control is
  // disabled (not silently absent) so the reason is explained rather than the option just
  // vanishing.
  const dismissDisabled = nonSuppressible || item.statutoryDeadline != null;
  const reasonText = nonSuppressible ? NON_SUPPRESSIBLE_REASON : DEADLINE_DISMISS_TOOLTIP;
  const dismissReasonId = `attention-dismiss-reason-${item.taskHash}`;
  const snoozeListboxId = `attention-snooze-listbox-${item.taskHash}`;
  // SNOOZE_OPTIONS is a fixed, non-empty constant, but index access is still `T | undefined`
  // under noUncheckedIndexedAccess — clamp once here so every read below is safely typed.
  const activeSnoozeOption = (SNOOZE_OPTIONS[activeSnoozeIndex] ?? SNOOZE_OPTIONS[0])!;
  const activeSnoozeOptionId = `attention-snooze-option-${item.taskHash}-${activeSnoozeOption.days}`;

  // FM2 — a true listbox keyboard model: one tab stop (the listbox itself), a roving
  // `aria-activedescendant`, and focus moved into the popup on open / back to the trigger on
  // close, so the declared `role="listbox"`/`role="option"` pattern matches actual behavior.
  useEffect(() => {
    if (snoozeOpen) {
      setActiveSnoozeIndex(0);
      snoozeListboxRef.current?.focus();
    }
  }, [snoozeOpen]);

  const closeSnooze = (returnFocus: boolean) => {
    setSnoozeOpen(false);
    if (returnFocus) snoozeTriggerRef.current?.focus();
  };

  const selectActiveSnoozeOption = () => {
    closeSnooze(true);
    onSnooze(item.taskHash, activeSnoozeOption.days);
  };

  const handleSnoozeListboxKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSnoozeIndex((i) => Math.min(i + 1, SNOOZE_OPTIONS.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveSnoozeIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setActiveSnoozeIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveSnoozeIndex(SNOOZE_OPTIONS.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        selectActiveSnoozeOption();
        break;
      case 'Escape':
        e.preventDefault();
        closeSnooze(true);
        break;
      case 'Tab':
        // Popup close-on-blur — don't trap Tab, just collapse the popup as focus leaves it.
        setSnoozeOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <li
      className="flex items-start gap-3 py-3 border-b border-border last:border-0"
      data-testid={`attention-item-${item.taskHash}`}
    >
      {/* Severity dot */}
      <div className="mt-1 shrink-0">
        <span
          className={`inline-block h-2.5 w-2.5 rounded-full ${
            item.severity === 'required'
              ? 'bg-destructive'
              : item.severity === 'recommended'
                ? 'bg-warning'
                : 'bg-muted-foreground'
          }`}
          aria-label={`Severity: ${item.severity}`}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-0.5">
          <span className="text-sm font-semibold text-foreground">{item.title}</span>
          <Badge
            variant="outline"
            className={`text-xs px-1.5 py-0 h-4 ${severityColor} border-current`}
          >
            {SEVERITY_LABELS[item.severity]}
          </Badge>
          {isHidden && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">
              {item.preference?.action === 'dismissed' ? 'Dismissed' : 'Snoozed'}
            </Badge>
          )}
          {/* BR-16 — a snooze on a deadline-bearing item is silently truncated server-side to
              the deadline; that truncation must be SURFACED to the user, not just applied
              invisibly (otherwise a landlord who asked for "30 days" has no way to learn they
              actually got 7). Only rendered when the server returned a snoozedUntil. */}
          {isHidden && item.preference?.action === 'snoozed' && item.preference?.snoozedUntil && (
            <span
              className="text-xs text-muted-foreground"
              data-testid={`attention-snoozed-until-${item.taskHash}`}
            >
              until {formatTimestamp(asIsoDateTimeString(item.preference.snoozedUntil), LOCAL_TZ, { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>

        {item.severityRationale && (
          <p className="text-xs text-muted-foreground mb-1">{item.severityRationale}</p>
        )}
        {item.description && (
          <p className="text-xs text-muted-foreground">{item.description}</p>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {/* Resolve link */}
          {item.actionUrl && (
            <Button
              variant="outline"
              size="sm"
              asChild
              className="h-11 px-3 text-xs"
              data-testid={`attention-resolve-${item.taskHash}`}
            >
              <Link to={item.actionUrl}>Resolve</Link>
            </Button>
          )}

          {/* Undo dismiss / show undo if hidden */}
          {isHidden ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-11 px-3 text-xs gap-1"
              disabled={isPending}
              aria-busy={isPending}
              onClick={() => onUndismiss(item.taskHash)}
              data-testid={`attention-undismiss-${item.taskHash}`}
            >
              <RotateCcw className="h-3 w-3" />
              {isPending ? 'Working…' : 'Undo'}
            </Button>
          ) : showActions ? (
            <>
              {/* Dismiss button — disabled (not hidden) with an explanatory tooltip for
                  deadline-bearing items, per BR-17. The reason is also always rendered as
                  visible text (below), not only a bare `title`, because a natively `disabled`
                  button is removed from the tab order and its `title` is unreachable for
                  keyboard-only/screen-reader users. */}
              <Button
                variant="ghost"
                size="sm"
                className="h-11 px-3 text-xs"
                disabled={isPending}
                aria-disabled={dismissDisabled}
                aria-busy={isPending}
                aria-describedby={dismissDisabled ? dismissReasonId : undefined}
                title={dismissDisabled ? reasonText : undefined}
                onClick={() => {
                  if (dismissDisabled) return;
                  onDismiss(item.taskHash);
                }}
                data-testid={`attention-dismiss-${item.taskHash}`}
              >
                {isPending ? 'Working…' : 'Dismiss'}
              </Button>

              {/* Snooze dropdown — the trigger is disabled outright (not just the dismiss
                  button) for a non-suppressible item (BR-31): there is no valid duration to
                  offer when the item can't be hidden at all. */}
              <div className="relative">
                <Button
                  ref={snoozeTriggerRef}
                  variant="ghost"
                  size="sm"
                  className="h-11 px-3 text-xs"
                  disabled={isPending}
                  aria-disabled={nonSuppressible}
                  aria-busy={isPending}
                  aria-describedby={nonSuppressible ? dismissReasonId : undefined}
                  title={nonSuppressible ? reasonText : undefined}
                  onClick={() => {
                    if (nonSuppressible) return;
                    setSnoozeOpen((o) => !o);
                  }}
                  data-testid={`attention-snooze-${item.taskHash}`}
                  aria-haspopup="listbox"
                  aria-expanded={snoozeOpen}
                  aria-controls={snoozeOpen ? snoozeListboxId : undefined}
                >
                  {isPending ? 'Working…' : 'Snooze'}
                </Button>
                {snoozeOpen && (
                  <>
                    {/* Backdrop to close on outside click */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => closeSnooze(false)}
                      aria-hidden="true"
                    />
                    <ul
                      id={snoozeListboxId}
                      ref={snoozeListboxRef}
                      role="listbox"
                      tabIndex={0}
                      aria-label={`Snooze duration for ${item.title}`}
                      aria-activedescendant={activeSnoozeOptionId}
                      onKeyDown={handleSnoozeListboxKeyDown}
                      className="absolute left-0 top-full mt-1 z-20 min-w-[8rem] rounded-md border border-border bg-popover shadow-md py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {SNOOZE_OPTIONS.map(({ days, label }, index) => (
                        <li
                          key={days}
                          id={`attention-snooze-option-${item.taskHash}-${days}`}
                          role="option"
                          aria-selected={index === activeSnoozeIndex}
                          onMouseEnter={() => setActiveSnoozeIndex(index)}
                          onClick={() => {
                            setActiveSnoozeIndex(index);
                            closeSnooze(true);
                            onSnooze(item.taskHash, days);
                          }}
                          data-testid={`attention-snooze-${item.taskHash}-${days}d`}
                          className={`min-h-[44px] flex items-center px-3 text-sm cursor-pointer ${
                            index === activeSnoozeIndex
                              ? 'bg-accent text-accent-foreground'
                              : ''
                          }`}
                        >
                          {label}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </>
          ) : null}
        </div>

        {dismissDisabled && !isHidden && showActions && (
          <p id={dismissReasonId} className="text-xs text-muted-foreground mt-1">
            {reasonText}
          </p>
        )}
      </div>

      {/* Trailing context — due/overdue/amount/count (US-7/US-8/US-10) */}
      <AttentionItemTrailing item={item} />
    </li>
  );
}

/* ── Loading Skeleton ── */

function AttentionSkeleton() {
  return (
    <div className="max-w-4xl mx-auto pb-6">
      <div className="px-[var(--page-padding-x)]">
        <div className="max-w-3xl mx-auto space-y-4">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-10 w-full rounded-lg" />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */

export function AttentionPage() {
  const [data, setData] = useState<AttentionItemsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [pendingHashes, setPendingHashes] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  // FM2 — after a dismiss/snooze/undo re-renders the list and removes the acted-on <li> (and
  // its buttons) from the DOM, focus otherwise falls through to <body>. Land it on this stable,
  // always-rendered landmark instead so keyboard/screen-reader users don't lose their place.
  const summaryRef = useRef<HTMLDivElement>(null);
  const focusSummaryAfterAction = useCallback(() => {
    // Defer one frame so the ref points at the post-refetch DOM before we focus it.
    requestAnimationFrame(() => summaryRef.current?.focus());
  }, []);

  /* ── Fetch ── */

  /**
   * Load attention items.
   * @param background - when true, skip the full-page skeleton and refresh data in-place.
   *   Use `background=true` for post-action refetches so the page stays mounted and
   *   UI elements remain stable for both users and tests.
   */
  const fetchData = useCallback(async (background = false) => {
    try {
      if (!background) {
        setIsLoading(true);
      }
      setError(null);
      const result = await getAttentionItems();
      setData(result);
      if (background) setRefreshCount((c) => c + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load attention items');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  /* ── Action Handlers ── */

  const handleDismiss = async (taskHash: string) => {
    setPendingHashes((prev) => new Set(prev).add(taskHash));
    try {
      await dismissAttentionItem(taskHash);
      await fetchData(true);
      void queryClient.invalidateQueries({ queryKey: ATTENTION_ITEMS_QUERY_KEY });
      focusSummaryAfterAction();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to dismiss item');
    } finally {
      setPendingHashes((prev) => {
        const s = new Set(prev);
        s.delete(taskHash);
        return s;
      });
    }
  };

  const handleSnooze = async (taskHash: string, days: 1 | 3 | 7 | 30) => {
    setPendingHashes((prev) => new Set(prev).add(taskHash));
    try {
      await snoozeAttentionItem(taskHash, days);
      await fetchData(true);
      void queryClient.invalidateQueries({ queryKey: ATTENTION_ITEMS_QUERY_KEY });
      focusSummaryAfterAction();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to snooze item');
    } finally {
      setPendingHashes((prev) => {
        const s = new Set(prev);
        s.delete(taskHash);
        return s;
      });
    }
  };

  const handleUndismiss = async (taskHash: string) => {
    setPendingHashes((prev) => new Set(prev).add(taskHash));
    try {
      await removeAttentionItemPreference(taskHash);
      await fetchData(true);
      void queryClient.invalidateQueries({ queryKey: ATTENTION_ITEMS_QUERY_KEY });
      focusSummaryAfterAction();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to undo dismissal');
    } finally {
      setPendingHashes((prev) => {
        const s = new Set(prev);
        s.delete(taskHash);
        return s;
      });
    }
  };

  if (isLoading) {
    return <AttentionSkeleton />;
  }

  /* ── Derived Data ── */

  const allItems = data?.items ?? [];
  const summary = data?.summary;
  const degraded = data?.degraded ?? [];

  // BR-13 — grouped by the SERVER-ASSIGNED band; already sorted within-band by the engine
  // (BR-24), so no client-side re-sort.
  const visibleItems = allItems.filter((i) => i.preference === null);
  const hiddenItems = allItems.filter((i) => i.preference !== null);
  const bandGroups = ATTENTION_BAND_ORDER.map((band) => ({
    band,
    items: visibleItems.filter((i) => i.band === band),
  }));

  const hasAnyItems = allItems.length > 0;
  const hasVisibleItems = visibleItems.length > 0;

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="attention-page" data-refresh-count={refreshCount}>
      <div className="px-[var(--page-padding-x)]">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold mb-6">Attention</h1>

          {/* Error banner */}
          {error && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm flex justify-between items-center"
            >
              <p className="text-destructive">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void fetchData()}
                className="ml-3 shrink-0 h-11 px-3"
                data-testid="attention-retry"
              >
                Retry
              </Button>
            </div>
          )}

          {/* Degraded notice (BR-25) — this surface must never present a partial list as
              complete. */}
          {degraded.length > 0 && (
            <div
              role="status"
              className="mb-4 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm"
              data-testid="attention-degraded-notice"
            >
              <p className="text-foreground">
                Some items could not be loaded right now, so this list may be incomplete.
              </p>
            </div>
          )}

          {/* Summary bar — also the FM2 focus-landing target after dismiss/snooze/undo (see
              focusSummaryAfterAction): a stable, always-rendered landmark that announces the
              updated counts instead of letting focus fall through to <body>. */}
          {summary && (
            <div
              ref={summaryRef}
              tabIndex={-1}
              role="status"
              aria-live="polite"
              className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="attention-summary"
            >
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="font-medium text-foreground">
                  {summary.visible} item{summary.visible !== 1 ? 's' : ''} remaining
                </span>
                {summary.dismissed > 0 && (
                  <span className="text-muted-foreground">
                    {summary.dismissed} dismissed
                  </span>
                )}
                {summary.snoozed > 0 && (
                  <span className="text-muted-foreground">
                    {summary.snoozed} snoozed
                  </span>
                )}
              </div>

              {hiddenItems.length > 0 && (
                <label
                  className="flex min-h-[44px] items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none"
                  data-testid="attention-show-hidden-toggle"
                >
                  <input
                    type="checkbox"
                    checked={showHidden}
                    onChange={(e) => setShowHidden(e.target.checked)}
                    className="rounded border-input"
                  />
                  Show dismissed/snoozed
                </label>
              )}
            </div>
          )}

          {/* Item list — grouped by band (BR-13) */}
          {!hasAnyItems ? (
            <EmptyState
              icon={ClipboardList}
              title="Nothing needs your attention"
              description="There are no attention items for your account."
              data-testid="attention-empty"
            />
          ) : !hasVisibleItems && hiddenItems.length > 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="All items dismissed"
              description="You have dismissed or snoozed every attention item. Toggle 'Show dismissed/snoozed' to review them."
              data-testid="attention-empty"
            />
          ) : (
            <div className="space-y-4" data-testid="attention-item-list">
              {bandGroups
                .filter(({ items }) => items.length > 0)
                .map(({ band, items }) => (
                  <section
                    key={band}
                    aria-label={BAND_LABELS[band]}
                    data-testid={`attention-band-${band}`}
                  >
                    <div className="mb-2 flex items-center gap-2 px-1">
                      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {BAND_LABELS[band]}
                      </h2>
                      <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 min-w-5 justify-center">
                        {items.length}
                      </Badge>
                    </div>
                    <ul role="list" className="rounded-lg border border-border bg-card px-4">
                      {items.map((item) => (
                        <ItemRow
                          key={item.taskHash}
                          item={item}
                          isPending={pendingHashes.has(item.taskHash)}
                          onDismiss={handleDismiss}
                          onSnooze={handleSnooze}
                          onUndismiss={handleUndismiss}
                          showActions
                        />
                      ))}
                    </ul>
                  </section>
                ))}
            </div>
          )}

          {/* Dismissed / snoozed items (collapsible section) */}
          {showHidden && hiddenItems.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
                Dismissed and Snoozed
              </div>
              <ul
                role="list"
                className="rounded-lg border border-border bg-card px-4"
              >
                {hiddenItems.map((item) => (
                  <ItemRow
                    key={item.taskHash}
                    item={item}
                    isPending={pendingHashes.has(item.taskHash)}
                    onDismiss={handleDismiss}
                    onSnooze={handleSnooze}
                    onUndismiss={handleUndismiss}
                    showActions={false}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AttentionPage;
